<script setup lang="ts" generic="T">
import { ref, computed, onBeforeUnmount, onMounted, nextTick } from 'vue'

import { eventEmitter } from '@/utils/events/event-emitter'

import { CommonEvent } from '@/constant/events'

import type { DialogConfig } from '@/types/common'

const dialogVisible = ref(false)
const configs = ref<DialogConfig | null>(null)
const timeoutIds = ref<ReturnType<typeof setTimeout>[]>([])

const mergedDialogProps = computed(() => {
  return {
    closeOnClickModal: true,
    showClose: true,
    ...configs.value?.dialogProps
  }
})

const openDialog = async (options: DialogConfig) => {
  let threshold = 0
  if (dialogVisible.value) {
    dialogVisible.value = false
    threshold = 200
  }
  const timeout = setTimeout(async () => {
    configs.value = options
    dialogVisible.value = true
    await nextTick()
    await options.onOpen?.()
  }, threshold)
  timeoutIds.value.push(timeout)
}

const closeDialog = () => {
  dialogVisible.value = false
  configs.value?.onClose?.()
  const timeout = setTimeout(() => {
    configs.value = null
  }, 200)
  timeoutIds.value.push(timeout)
}

onMounted(() => {
  eventEmitter.on(CommonEvent.OPEN_DIALOG, openDialog)
  eventEmitter.on(CommonEvent.CLOSE_DIALOG, closeDialog)
})

onBeforeUnmount(() => {
  eventEmitter.off(CommonEvent.OPEN_DIALOG, openDialog)
  eventEmitter.off(CommonEvent.CLOSE_DIALOG, closeDialog)
  timeoutIds.value.forEach(clearTimeout)
})
</script>

<template>
  <el-dialog destroy-on-close :model-value="dialogVisible" v-bind="mergedDialogProps" class="global-dialog">
    <template v-if="configs?.dialogSlots?.header || configs?.title" #header>
      <component :is="configs.dialogSlots.header" v-if="configs?.dialogSlots?.header" />
      <h3
        v-else-if="configs?.title"
        class="whitespace-pre-line break-all text-left text-lg font-bold leading-6 tracking-[0.18px] text-app-black-900"
        v-text="configs?.title"
      />
    </template>
    <template v-if="configs?.children">
      <el-scrollbar v-if="configs?.scrollable" :style="{ height: configs?.height || 'auto' }">
        <component :is="configs?.children" />
      </el-scrollbar>
      <div v-else>
        <component :is="configs?.children" />
      </div>
    </template>
    <template v-if="configs?.dialogSlots?.footer" #footer>
      <component :is="configs.dialogSlots.footer" />
    </template>
  </el-dialog>
</template>
